import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "offer-banner-dismissed";

const calculateTimeLeft = (targetDate) => {
  const difference = new Date(targetDate) - new Date();
  if (difference <= 0) return null;
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

const pad = (n) => String(n).padStart(2, "0");

const getDefaultTargetDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString();
};

const OfferBanner = ({
  message = "Limited Time: Free consultation for new clients!",
  targetDate = getDefaultTargetDate(),
  linkText = "Book Now",
  linkHref = "/contact",
}) => {
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetDate));

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft(targetDate);
      setTimeLeft(remaining);
      if (!remaining) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  }, []);

  if (!visible || !timeLeft) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50"
      style={{ background: "linear-gradient(90deg, #FB5B38, #FF7C53)" }}
      role="banner"
      aria-label="Limited time offer"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 sm:px-6 flex items-center justify-center gap-2 sm:gap-4 relative">
        {/* Message */}
        <p className="font-outfit text-white text-xs sm:text-sm truncate max-w-[45%] sm:max-w-none">
          {message}
        </p>

        {/* Countdown inline */}
        <span
          className="font-poppins font-semibold text-white text-xs sm:text-sm tracking-wide whitespace-nowrap"
          role="timer"
          aria-label={`${timeLeft.days} days, ${timeLeft.hours} hours, ${timeLeft.minutes} minutes, ${timeLeft.seconds} seconds remaining`}
        >
          {pad(timeLeft.days)}:{pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:
          {pad(timeLeft.seconds)}
        </span>

        {/* CTA link */}
        <a
          href={linkHref}
          className="hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#FB5B38] font-poppins font-medium text-xs sm:text-sm
            hover:bg-white/90 transition-colors whitespace-nowrap"
        >
          {linkText} &rarr;
        </a>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss offer banner"
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 rounded
            hover:bg-white/20 transition-colors
            focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-white"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default OfferBanner;
